import { useEffect, useMemo, useState } from "react";
import type {
  AdminEvent,
  AdminEventInput,
  EventCategory,
  LocationKey,
  VolunteerRole,
} from "../../../shared/types";
import { ROLE_LABELS } from "../../../shared/types";
import {
  adminCreateEvent,
  adminDeleteEvent,
  adminEventRoster,
  adminListEvents,
  adminUpdateEvent,
} from "../../api";
import { monthLabel, weekdayOf } from "../../lib/hours";

type Draft = AdminEventInput;

const BLANK: Draft = {
  category: "morning_reading",
  title: "",
  location: "",
  locationKey: "other",
  eventDate: null,
  dateConfirmed: true,
  startTime: "13:20",
  endTime: "14:50",
  creditHours: 2,
  format: "實體",
  eligibleRoles: ["english_seed", "activity"],
  capacitySeed: 1,
  capacityActivity: 2,
  note: "",
  isOpen: true,
  monthGroup: "",
  sortOrder: 0,
};

function toDraft(e: AdminEvent): Draft {
  return {
    category: e.category,
    title: e.title,
    location: e.location ?? "",
    locationKey: e.locationKey,
    eventDate: e.eventDate,
    dateConfirmed: e.dateConfirmed,
    startTime: e.startTime,
    endTime: e.endTime,
    creditHours: e.creditHours,
    format: e.format,
    eligibleRoles: e.eligibleRoles,
    capacitySeed: e.capacitySeed,
    capacityActivity: e.capacityActivity,
    note: e.note ?? "",
    isOpen: e.isOpen,
    monthGroup: e.monthGroup,
    sortOrder: e.sortOrder,
  };
}

export default function EventsTab() {
  const [events, setEvents] = useState<AdminEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<AdminEvent | "new" | null>(null);
  const [rosterFor, setRosterFor] = useState<AdminEvent | null>(null);

  async function load() {
    setLoading(true);
    try {
      const r = await adminListEvents();
      setEvents(r.events);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "載入失敗");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
  }, []);

  const grouped = useMemo(() => {
    const map = new Map<string, AdminEvent[]>();
    for (const e of events) {
      if (!map.has(e.monthGroup)) map.set(e.monthGroup, []);
      map.get(e.monthGroup)!.push(e);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [events]);

  async function toggleOpen(e: AdminEvent) {
    await adminUpdateEvent(e.id, { ...toDraft(e), isOpen: !e.isOpen });
    load();
  }

  const capCell = (confirmed: number, cap: number | null, wait: number) => {
    if (cap == null) return `${confirmed}／不限`;
    const s = `${Math.min(confirmed, cap)}／${cap}`;
    return wait > 0 ? `${s}・遞補 ${wait}` : s;
  };

  return (
    <div>
      <div className="toolbar">
        <span className="muted">共 {events.length} 場</span>
        <span className="muted" style={{ fontSize: 12 }}>
          場次由計畫初始設定；此頁用來<strong>編輯名額 / 時間 / 開關 / 看名單</strong>，不新增全新場次。
        </span>
      </div>
      {err && <div className="alert err">{err}</div>}
      {loading ? (
        <p>載入中…</p>
      ) : (
        grouped.map(([mg, list]) => (
          <div key={mg} style={{ marginBottom: 18 }}>
            <h3>{monthLabel(mg)}</h3>
            <div className="table-wrap">
              <table className="grid">
                <thead>
                  <tr>
                    <th>日期</th>
                    <th>場次 / 地點</th>
                    <th>時間</th>
                    <th>時數</th>
                    <th>類型</th>
                    <th>種子教師（正取／名額）</th>
                    <th>活動志工（正取／名額）</th>
                    <th>名單</th>
                    <th>狀態</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((e) => (
                    <tr key={e.id}>
                      <td>
                        {e.eventDate ?? <span className="tag warn">日期待確認</span>}
                        {e.eventDate && (
                          <>
                            <br />
                            <span className="muted">{weekdayOf(e.eventDate)}</span>
                          </>
                        )}
                      </td>
                      <td>
                        {e.title}
                        <br />
                        <span className="muted">{e.location || "—"}</span>
                      </td>
                      <td>{e.startTime && e.endTime ? `${e.startTime}–${e.endTime}` : "—"}</td>
                      <td>{e.creditHours}h</td>
                      <td>{e.category === "charity" ? "星星國公益活動" : "AR伴讀"}</td>
                      <td>{capCell(e.confirmedSeed, e.capacitySeed, e.waitlistSeed)}</td>
                      <td>{capCell(e.confirmedActivity, e.capacityActivity, e.waitlistActivity)}</td>
                      <td>
                        <button className="btn ghost sm" onClick={() => setRosterFor(e)}>
                          {e.selectedCount} 人
                        </button>
                      </td>
                      <td>
                        <button
                          className={`btn sm ${e.isOpen ? "ghost" : "danger"}`}
                          onClick={() => toggleOpen(e)}
                        >
                          {e.isOpen ? "開放中" : "已關閉"}
                        </button>
                      </td>
                      <td>
                        <button className="btn ghost sm" onClick={() => setEditing(e)}>
                          編輯
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}

      {!loading && (
        <div
          style={{
            background: "var(--bg)",
            border: "1px solid var(--line)",
            borderRadius: 10,
            padding: "10px 14px",
            fontSize: 11,
            lineHeight: 1.7,
            color: "var(--ink-faint)",
            marginTop: 16,
          }}
        >
          <strong style={{ color: "var(--ink-soft)" }}>場次管理的資料依據</strong>
          <br />
          ・場次清單存在本系統資料庫（Cloudflare D1）的 <code>events</code> 表；每場欄位：類型、名稱、地點、日期、時間、認列時數、名額（英文種子教師／活動志工分開）、開放狀態、月份分組、排序、備註。
          <br />
          ・初始資料由 <code>seed.sql</code> 灌入（2026 上學期：AR 伴讀-稻香／吉安每雙週四 10 次、星星國公益 3 場）。<strong>正式上線後一律在本分頁增修，不再重跑 seed</strong>（重跑會清掉學生已勾選的紀錄）。
          <br />
          ・「正取／名額・遞補」由系統即時計算：同名額多人勾選時，依「勾選送出時間」先到先得，名額內為正取、其餘為遞補 1、2、3…，不另存欄位。
          <br />
          ・本分頁的新增／編輯／刪除／開關<strong>會立即寫回資料庫</strong>，並同步影響「排班」與「志工資料」分頁的計算（那兩頁為唯讀）。
        </div>
      )}

      {editing && (
        <EventModal
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
      {rosterFor && <RosterModal event={rosterFor} onClose={() => setRosterFor(null)} />}
    </div>
  );
}

function EventModal({
  initial,
  onClose,
  onSaved,
}: {
  initial: AdminEvent | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [d, setD] = useState<Draft>(initial ? toDraft(initial) : BLANK);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function upd<K extends keyof Draft>(k: K, v: Draft[K]) {
    setD((s) => ({ ...s, [k]: v }));
  }

  function onDate(v: string) {
    upd("eventDate", v || null);
    if (v) {
      upd("monthGroup", v.slice(0, 7));
      upd("dateConfirmed", true);
    }
  }

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const payload = { ...d, location: d.location || null, note: d.note || null };
      if (initial) await adminUpdateEvent(initial.id, payload);
      else await adminCreateEvent(payload);
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "儲存失敗");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!initial) return;
    if (!confirm(`刪除場次「${initial.title}」？已勾選此場次的紀錄會一併移除。`)) return;
    setBusy(true);
    try {
      await adminDeleteEvent(initial.id);
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "刪除失敗");
      setBusy(false);
    }
  }

  const numOrNull = (v: string) => (v === "" ? null : Math.max(0, Number(v)));

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>{initial ? `編輯場次 · #${initial.id}` : "新增場次"}</h3>
        {err && <div className="alert err">{err}</div>}

        <div className="field">
          <label>場次名稱</label>
          <input value={d.title} onChange={(e) => upd("title", e.target.value)} />
        </div>

        <div className="row2">
          <div className="field">
            <label>類型</label>
            <select
              value={d.category}
              onChange={(e) => upd("category", e.target.value as EventCategory)}
            >
              <option value="morning_reading">AR伴讀</option>
              <option value="charity">星星國公益活動</option>
            </select>
          </div>
          <div className="field">
            <label>篩選分類</label>
            <select
              value={d.locationKey}
              onChange={(e) => upd("locationKey", e.target.value as LocationKey)}
            >
              <option value="daoxiang">AR伴讀-稻香國小</option>
              <option value="jian">AR伴讀-吉安國小</option>
              <option value="charity">星星國-公益活動</option>
              <option value="other">其他</option>
            </select>
          </div>
        </div>

        <div className="row2">
          <div className="field">
            <label>日期（留空 = 日期待確認）</label>
            <input type="date" value={d.eventDate ?? ""} onChange={(e) => onDate(e.target.value)} />
          </div>
          <div className="field">
            <label>月份分組（YYYY-MM）</label>
            <input
              value={d.monthGroup}
              onChange={(e) => upd("monthGroup", e.target.value)}
              placeholder="2026-10"
            />
          </div>
        </div>

        <div className="row2">
          <div className="field">
            <label>開始時間</label>
            <input
              type="time"
              value={d.startTime ?? ""}
              onChange={(e) => upd("startTime", e.target.value || null)}
            />
          </div>
          <div className="field">
            <label>結束時間</label>
            <input
              type="time"
              value={d.endTime ?? ""}
              onChange={(e) => upd("endTime", e.target.value || null)}
            />
          </div>
        </div>

        <div className="row2">
          <div className="field">
            <label>服務認列時數</label>
            <input
              type="number"
              step="0.5"
              min="0"
              value={d.creditHours}
              onChange={(e) => upd("creditHours", Number(e.target.value))}
            />
          </div>
          <div className="field">
            <label>形式</label>
            <input value={d.format} onChange={(e) => upd("format", e.target.value)} />
          </div>
        </div>

        <div className="row2">
          <div className="field">
            <label>英文種子教師名額（留空 = 不限）</label>
            <input
              type="number"
              min="0"
              value={d.capacitySeed ?? ""}
              onChange={(e) => upd("capacitySeed", numOrNull(e.target.value))}
            />
          </div>
          <div className="field">
            <label>活動志工名額（留空 = 不限）</label>
            <input
              type="number"
              min="0"
              value={d.capacityActivity ?? ""}
              onChange={(e) => upd("capacityActivity", numOrNull(e.target.value))}
            />
          </div>
        </div>

        <div className="field">
          <label>地點</label>
          <input value={d.location ?? ""} onChange={(e) => upd("location", e.target.value)} />
        </div>

        <div className="field">
          <label>適用身分</label>
          <div className="checks">
            {(Object.keys(ROLE_LABELS) as VolunteerRole[]).map((r) => (
              <label key={r} className={`check${d.eligibleRoles.includes(r) ? " on" : ""}`}>
                <input
                  type="checkbox"
                  checked={d.eligibleRoles.includes(r)}
                  onChange={() =>
                    upd(
                      "eligibleRoles",
                      d.eligibleRoles.includes(r)
                        ? d.eligibleRoles.filter((x) => x !== r)
                        : [...d.eligibleRoles, r],
                    )
                  }
                />
                {ROLE_LABELS[r]}
              </label>
            ))}
          </div>
        </div>

        <div className="field">
          <label>備註</label>
          <textarea value={d.note ?? ""} onChange={(e) => upd("note", e.target.value)} />
        </div>

        <div className="row2">
          <label className="check">
            <input
              type="checkbox"
              checked={d.isOpen}
              onChange={(e) => upd("isOpen", e.target.checked)}
            />
            開放勾選
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={d.dateConfirmed}
              onChange={(e) => upd("dateConfirmed", e.target.checked)}
            />
            日期已確認
          </label>
        </div>

        <div className="spacer" />
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn primary" style={{ flex: 1 }} disabled={busy} onClick={save}>
            {busy ? "儲存中…" : "儲存"}
          </button>
          <button className="btn ghost" disabled={busy} onClick={onClose}>
            取消
          </button>
          {initial && (
            <button className="btn danger" disabled={busy} onClick={remove}>
              刪除
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function RosterModal({ event, onClose }: { event: AdminEvent; onClose: () => void }) {
  const [list, setList] = useState<
    Array<{
      id: number;
      name: string;
      primaryEmail: string;
      phone: string;
      status: string;
      bucket: string;
      waitlistPosition: number;
    }>
  >([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminEventRoster(event.id)
      .then((r) => setList(r.roster))
      .finally(() => setLoading(false));
  }, [event.id]);

  const bucketLabel = (b: string) => (b === "seed" ? "英文種子教師" : "活動志工");

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>
          {event.title} · 名單（{event.selectedCount} 人）
        </h3>
        <p className="muted">
          {event.eventDate ?? "日期待確認"}
          {event.location ? `｜${event.location}` : ""}｜種子教師 {event.confirmedSeed}/
          {event.capacitySeed ?? "∞"}・志工 {event.confirmedActivity}/{event.capacityActivity ?? "∞"}
        </p>
        {loading ? (
          <p>載入中…</p>
        ) : list.length === 0 ? (
          <p className="muted">尚無人勾選此場次。</p>
        ) : (
          <div className="table-wrap">
            <table className="grid">
              <thead>
                <tr>
                  <th>身分別</th>
                  <th>錄取</th>
                  <th>姓名</th>
                  <th>Email</th>
                  <th>手機</th>
                  <th>狀態</th>
                </tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.id}>
                    <td>{bucketLabel(r.bucket)}</td>
                    <td>
                      {r.waitlistPosition === 0 ? (
                        <span className="tag ok">正取</span>
                      ) : (
                        <span className="tag warn">遞補 {r.waitlistPosition}</span>
                      )}
                    </td>
                    <td>{r.name}</td>
                    <td style={{ wordBreak: "break-all" }}>{r.primaryEmail}</td>
                    <td>{r.phone}</td>
                    <td>{r.status === "submitted" ? "已確認" : "草稿"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="spacer" />
        <button className="btn ghost full" onClick={onClose}>
          關閉
        </button>
      </div>
    </div>
  );
}
